import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { ThemeService, RecorderTheme, liftToLight } from '../../services/theme.service';

interface ThemeGroup {
  name: string;
  themes: RecorderTheme[];
  open: boolean;
}

const ALL_CATEGORY = 'All';

const CATEGORY_MAP: { name: string; ids: string[] }[] = [
  { name: 'Starters',     ids: ['default', 'claude-colorblind', 'aurora', 'ember', 'slate', 'cool', 'warm', 'romantic'] },
  { name: 'Music & Stage',ids: ['synthwave', 'jazz-club', 'vinyl-retro', 'rock-stage', 'neon-tokyo', 'disco', 'edm-rave', 'studio-pro'] },
  { name: 'Live',         ids: ['pulse', 'jingle', 'tinsel', 'wave', 'glow', 'drift', 'breathe', 'aurora-live', 'sunrise'] },
];

@Component({
  selector: 'rec-theme-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TooltipDirective],
  templateUrl: './theme-picker.component.html',
  styleUrls: ['./theme-picker.component.scss'],
})
export class ThemePickerComponent implements OnInit, OnDestroy {
  readonly themeService = inject(ThemeService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Output() readonly commit = new EventEmitter<void>();
  /** Fires whenever the theme builder opens or closes — lets the parent
   *  suppress click-outside auto-close while the user is editing. */
  @Output() readonly builderActive = new EventEmitter<boolean>();

  groups: ThemeGroup[] = [];

  // ── Search + category filter ──────────────────────────────────────────────
  searchQuery = '';
  selectedCategory: string = ALL_CATEGORY;

  /** Live animation tick (ms since component init). Used by cardBg() to
   *  drive the Live theme card backgrounds. Updated ~5fps so animations
   *  read as smooth on the small cards without burning CPU. */
  private _animMs = 0;
  private _animTimer: ReturnType<typeof setInterval> | null = null;
  private _animStart = Date.now();

  get activeId(): string {
    return this.themeService.activeThemeId;
  }

  ngOnInit(): void {
    this._buildGroups();
    // Default the category pill to whichever category contains the active
    // theme — so opening the picker lands the user on their current theme
    // instead of the full "All" list.
    const active = this.groups.find(g => g.themes.some(t => t.id === this.activeId));
    if (active) this.selectedCategory = active.name;
    this._animTimer = setInterval(() => {
      this._animMs = Date.now() - this._animStart;
      this.cdr.markForCheck();
    }, 200);
  }

  ngOnDestroy(): void {
    if (this._animTimer) {
      clearInterval(this._animTimer);
      this._animTimer = null;
    }
  }

  private _buildGroups(): void {
    const allThemes = this.themeService.themes;
    const categorised = new Set<string>();
    const activeId = this.activeId;

    const groups: ThemeGroup[] = CATEGORY_MAP.map(cat => {
      const themes = cat.ids
        .map(id => allThemes.find(t => t.id === id))
        .filter((t): t is RecorderTheme => !!t);
      themes.forEach(t => categorised.add(t.id));
      const containsActive = themes.some(t => t.id === activeId);
      return { name: cat.name, themes, open: containsActive };
    });

    // Custom / imported themes not in any category
    const custom = allThemes.filter(t => !categorised.has(t.id));
    if (custom.length) {
      groups.push({ name: 'My Themes', themes: custom, open: custom.some(t => t.id === activeId) });
    }

    this.groups = groups;
  }

  toggleGroup(group: ThemeGroup): void {
    group.open = !group.open;
    this.cdr.markForCheck();
  }

  /** Pill row entries: "All" + every non-empty group. */
  get categoryNames(): string[] {
    return [ALL_CATEGORY, ...this.groups.filter(g => g.themes.length).map(g => g.name)];
  }

  selectCategory(name: string): void {
    this.selectedCategory = name;
    this.cdr.markForCheck();
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.selectedCategory = ALL_CATEGORY;
    this.cdr.markForCheck();
  }

  /** Themes matching the current category pill + (case-insensitive) search. */
  get filteredThemes(): RecorderTheme[] {
    const q = this.searchQuery.trim().toLowerCase();
    const groups = this.selectedCategory === ALL_CATEGORY
      ? this.groups
      : this.groups.filter(g => g.name === this.selectedCategory);
    const themes: RecorderTheme[] = [];
    for (const g of groups) {
      for (const t of g.themes) {
        if (!q || t.name.toLowerCase().includes(q)) themes.push(t);
      }
    }
    return themes;
  }

  select(theme: RecorderTheme): void {
    this.themeService.applyTheme(theme);
    this._buildGroups();
    this.cdr.markForCheck();
  }

  selectAndCommit(theme: RecorderTheme): void {
    this.select(theme);
    this.commit.emit();
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.loadError = null;
      try {
        const data = JSON.parse(e.target?.result as string);
        if (
          !data ||
          typeof data !== 'object' ||
          typeof data.tokens !== 'object' ||
          !Object.keys(data.tokens ?? {}).some((k: string) => k.startsWith('--'))
        ) {
          this.loadError = 'Not a valid SingSnap theme file.';
          this.cdr.markForCheck();
          return;
        }
        const theme = this.themeService.importTheme(data);
        this.themeService.applyTheme(theme);
        this._buildGroups();
      } catch {
        this.loadError = 'Could not read the file. Make sure it is a valid .json file.';
      }
      this.cdr.markForCheck();
    };
    reader.readAsText(file);
    (event.target as HTMLInputElement).value = '';
  }

  deleteCustom(event: MouseEvent, theme: RecorderTheme): void {
    event.stopPropagation();
    this.themeService.deleteCustomTheme(theme.id);
    this._buildGroups();
    this.cdr.markForCheck();
  }

  downloadTheme(event: MouseEvent | null, theme: RecorderTheme): void {
    event?.stopPropagation();
    const data = JSON.stringify({ name: theme.name, tokens: theme.tokens }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${theme.name.replace(/\s+/g, '-').toLowerCase()}.singsnap-theme.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  previewColors(theme: RecorderTheme) {
    return this.themeService.previewColors(theme);
  }

  cardBg(theme: RecorderTheme): string {
    // Live themes: drive the card with the actual animation function so the
    // thumbnail mirrors what the recorder will look like in real time.
    if (theme.animation) {
      const tokens = theme.animation(this._animMs);
      const animBg = tokens['--shell-bg'];
      if (animBg) return animBg;
    }
    if (theme.fillLight) {
      const [dr, dg, db] = theme.fillLight.dark;
      const [lr, lg, lb] = theme.fillLight.light;
      return `linear-gradient(to right, rgb(${dr},${dg},${db}), rgb(${lr},${lg},${lb}))`;
    }
    const shellBg = theme.tokens['--shell-bg'];
    if (shellBg) {
      return shellBg.replace(/#[0-9a-fA-F]{6}/g, hex => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const boost = (v: number) => Math.min(255, Math.round(v * 4)).toString(16).padStart(2, '0');
        return `#${boost(r)}${boost(g)}${boost(b)}`;
      });
    }
    return theme.tokens['--modal-card-bg'] ?? '#1e2235';
  }

  cardLabelColor(theme: RecorderTheme): string {
    let r: number, g: number, b: number;
    if (theme.fillLight) {
      const [dr, dg, db] = theme.fillLight.dark;
      const [lr, lg, lb] = theme.fillLight.light;
      r = Math.round((dr + lr) / 2);
      g = Math.round((dg + lg) / 2);
      b = Math.round((db + lb) / 2);
    } else {
      const shellBg = theme.tokens['--shell-bg'];
      const hex = (shellBg?.match(/#[0-9a-fA-F]{6}/)?.[0]) ??
                  theme.tokens['--modal-card-bg'] ?? '#1e2235';
      r = Math.min(255, parseInt(hex.slice(1, 3), 16) * 4);
      g = Math.min(255, parseInt(hex.slice(3, 5), 16) * 4);
      b = Math.min(255, parseInt(hex.slice(5, 7), 16) * 4);
    }
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.45 ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.88)';
  }

  isAnimated(theme: RecorderTheme): boolean {
    return !!theme.animation;
  }

  // ── Theme builder ────────────────────────────────────────────────────────
  loadError: string | null = null;

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.loadError) {
      this.loadError = null;
      this.cdr.markForCheck();
    }
  }

  advancedOpen = false;
  builderOpen = false;
  builderName = '';
  builderBg        = '#041c0c';
  builderAccent    = '#58c7c9';
  builderHighlight = '#e35887';

  /** Optional explicit bright-fill endpoint (advanced builder option). */
  builderUseFillLight = false;
  builderBright       = '#ff96d2';

  /**
   * Counteract the browser's "scroll focused input into view" behavior when
   * a color picker opens. We schedule a scroll back to the builder's top so
   * Name + Save stay visible, then anchor the builder for both Chromium and
   * Safari timing.
   */
  onColorInputFocus(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const builder = document.querySelector('.theme-builder') as HTMLElement | null;
      builder?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }));
  }

  /** Cancel the active create/edit — restore the previous theme and close. */
  cancelBuilder(): void {
    if (!this.builderOpen) return;
    this.builderOpen = false;
    this.editingThemeId = null;
    this._restoreSaved();
    this.advancedOpen = false;
    this.builderActive.emit(false);
    this.cdr.markForCheck();
  }

  toggleBuilderFillLight(): void {
    this.builderUseFillLight = !this.builderUseFillLight;
    // On first enable, seed Bright with a lifted version of the current BG —
    // gives the user a sensible Romantic-style starting point instead of the
    // default pink that has nothing to do with their theme.
    if (this.builderUseFillLight) {
      const [r, g, b] = liftToLight(this.builderBg);
      const hex = (v: number) => v.toString(16).padStart(2, '0');
      this.builderBright = `#${hex(r)}${hex(g)}${hex(b)}`;
    }
    this._livePreview();
    this.cdr.markForCheck();
    // Re-scroll Save into view — toggling the 4th picker grows/shrinks the
    // builder and Save can fall below the fold.
    this._scrollSaveIntoView();
  }

  /**
   * After the builder opens (or grows when Bright is added), scroll so both
   * the theme name input (top of builder) and the Save button (bottom) are
   * in view. If the builder is taller than the viewport, anchor at the top
   * so the name input is visible; otherwise let the browser nudge it into
   * view naturally.
   */
  private _scrollSaveIntoView(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const builder = document.querySelector('.theme-builder') as HTMLElement | null;
      const saveBtn = document.querySelector('.theme-builder__save') as HTMLElement | null;
      const scrollable = builder?.closest('.panel__body') as HTMLElement | null;
      if (!builder || !saveBtn || !scrollable) {
        // Fallback: just bring Save into view.
        saveBtn?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
      }
      // If the whole builder fits in the visible area, scroll it so the top
      // aligns near the top of the viewport (showing both name + save).
      // Otherwise just keep Save in view so the user can act.
      const builderH = builder.getBoundingClientRect().height;
      const viewportH = scrollable.clientHeight;
      if (builderH <= viewportH - 24) {
        builder.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        saveBtn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }));
  }

  private _savedThemeId: string | null = null;
  /** ID of the theme being edited; null when creating a new theme. */
  editingThemeId: string | null = null;

  get builderSaveLabel(): string {
    if (!this.editingThemeId) return 'Save Theme';
    const t = this.themeService.themes.find(th => th.id === this.editingThemeId);
    return t && !t.builtIn ? 'Update Theme' : 'Save as New';
  }

  get builderContextLabel(): string | null {
    if (!this.editingThemeId) return null;
    const t = this.themeService.themes.find(th => th.id === this.editingThemeId);
    return t ? t.name : null;
  }

  toggleAdvanced(): void {
    // While the user is in the middle of creating / editing a theme, the
    // Advanced toggle is inert — Advanced only closes once the theme is
    // saved or the builder is cancelled.
    if (this.builderOpen) return;

    this.advancedOpen = !this.advancedOpen;
    // When opening, scroll the Advanced toggle to the top of the panel so
    // the just-revealed Export/Load/Edit/Create buttons are in view.
    if (this.advancedOpen) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const toggle = document.querySelector('.theme-advanced-toggle') as HTMLElement | null;
        toggle?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }));
    }
  }

  toggleBuilder(): void {
    this.builderOpen = !this.builderOpen;
    if (this.builderOpen) {
      this.editingThemeId = null;
      this._savedThemeId = this.themeService.activeThemeId;
      this._livePreview();
      this._scrollSaveIntoView();
    } else {
      // Cancellation — restore previous theme and collapse Advanced.
      this.editingThemeId = null;
      this._restoreSaved();
      this.advancedOpen = false;
    }
    this.builderActive.emit(this.builderOpen);
    this.cdr.markForCheck();
  }

  editTheme(): void {
    const theme = this.themeService.activeTheme;
    this.editingThemeId = theme.id;
    this._savedThemeId = theme.id;

    // Extract the three seed colors from the theme's tokens
    this.builderBg        = theme.tokens['--no-cam-bg']   ?? theme.tokens['--shell-surface'] ?? '#041c0c';
    this.builderAccent    = theme.tokens['--ss-mint']      ?? theme.tokens['--ui-accent']     ?? '#58c7c9';
    this.builderHighlight = theme.tokens['--ss-pink']      ?? '#e35887';

    // Pre-fill the bright endpoint if this theme has explicit fillLight.
    if (theme.fillLight) {
      const [lr, lg, lb] = theme.fillLight.light;
      const hex = (v: number) => v.toString(16).padStart(2, '0');
      this.builderBright = `#${hex(lr)}${hex(lg)}${hex(lb)}`;
      this.builderUseFillLight = true;
    } else {
      this.builderUseFillLight = false;
    }

    // Pre-fill name: custom themes keep their name; built-ins get a copy suffix
    this.builderName = theme.builtIn ? `${theme.name} (custom)` : theme.name;

    this.builderOpen = true;
    this.builderActive.emit(true);
    this._livePreview();
    this._scrollSaveIntoView();
    this.cdr.markForCheck();
  }

  onBuilderColorChange(field: 'bg' | 'accent' | 'highlight' | 'bright', value: string): void {
    if (field === 'bg')        this.builderBg = value;
    if (field === 'accent')    this.builderAccent = value;
    if (field === 'highlight') this.builderHighlight = value;
    if (field === 'bright')    this.builderBright = value;
    this._livePreview();
  }

  private _livePreview(): void {
    const preview = this.themeService.buildTheme(
      this.builderName.trim() || 'Preview',
      this.builderBg,
      this.builderAccent,
      this.builderHighlight,
      this.builderUseFillLight ? this.builderBright : undefined,
    );
    this.themeService.previewTokens(preview.tokens);
  }

  private _restoreSaved(): void {
    if (!this._savedThemeId) return;
    const prev = this.themeService.themes.find(t => t.id === this._savedThemeId);
    if (prev) this.themeService.applyTheme(prev);
    this._savedThemeId = null;
    this._buildGroups();
  }

  saveTheme(): void {
    if (!this.builderName.trim()) return;
    const built = this.themeService.buildTheme(
      this.builderName.trim(),
      this.builderBg,
      this.builderAccent,
      this.builderHighlight,
      this.builderUseFillLight ? this.builderBright : undefined,
    );
    this._savedThemeId = null;

    const editTarget = this.editingThemeId
      ? this.themeService.themes.find(t => t.id === this.editingThemeId)
      : null;

    if (editTarget && !editTarget.builtIn) {
      // Overwrite existing custom theme — preserve its id
      const updated: typeof built = { ...built, id: editTarget.id };
      this.themeService.updateTheme(updated);
      this.themeService.applyTheme(updated);
    } else {
      // New theme (create or fork of built-in)
      this.themeService.saveTheme(built);
      this.themeService.applyTheme(built);
    }

    this.editingThemeId = null;
    this.builderOpen = false;
    this.advancedOpen = false;
    this.builderActive.emit(false);
    this.builderName = '';
    this._buildGroups();
    this.cdr.markForCheck();
  }
}
